// Financial-year governance (v2's house icon) — years DERIVE from the company's FY
// start (day+month) + the transaction span; locking a year red-locks it (submitted to
// the receiver: nothing in range may change — enforced by the DB trigger, refusals are
// friendly). Lock/unlock is audit-trailed (who/when/why) in audit.period_lock.
//
// TWO tabs (owner 2026-09-19): Financial years (lock/unlock/close) and VAT returns
// (the VAT201 model — file a return, it freezes; a later adjustment flows into the NEXT
// return, never back into a filed one). Governance WRITES are gated on can_govern —
// resolved server-side by WHO MANAGES THE BOOKS (practice- vs self-managed). We read the
// same predicate the API's _gate uses via GET /expense-access (can_govern); the DB
// grant-guard means a business user on a practice-managed company never holds can_govern,
// so a non-governor sees state + the locked indicators but NONE of the controls.
//
// NB the backend is path-param, NOT body {fy_year}:
//   POST …/years/{fy}/lock   {close?}         — NO reason on lock; close=true posts closing journals
//   POST …/years/{fy}/unlock {reason}         — reason REQUIRED (API 400s on empty)
//   POST …/vat/submissions   {from,to,ref?}   — files + freezes
//   POST …/vat/submissions/{id}/reverse {reason} — reason REQUIRED
import { html, signal } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { tenantShell, leaveGovernance } from './tshell.js';
import { fmtMoney } from './bank_helpers.js';
import { t } from '../shared/i18n.js';
import { getScroll, setScroll } from '../shared/vtnav.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import '../shared/sleekdate.js';   // registers <sleek-date>

// Local dates, as document_editor.js builds them. toISOString() gives the UTC date: in Cape Town (UTC+2) the
// VAT period opened on the last day of the previous month, and before 02:00 "today" was yesterday (2026-10-09).
const isoLocal = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const isoToday = () => isoLocal(new Date());
const isoMonthStart = () => { const d = new Date(); return isoLocal(new Date(d.getFullYear(), d.getMonth(), 1)); };

export function governanceScreen(companyId){
  const base = '/companies/' + companyId + '/governance';
  const tab = signal('years');
  const note = signal('');
  // capability: the SAME can_govern the API's governance gate enforces. Non-governors get a
  // read-only view (state + locked indicators, no controls). Defaults closed until it loads.
  const caps = signal(null);
  (async () => { try { caps.value = await api.get('/companies/' + companyId + '/expense-access'); } catch { /* keep closed */ } })();
  const canGovern = () => !!(caps.value && caps.value.can_govern);
  // Feed <sleek-date> the company's date format + financial periods (uniform, company-format dates).
  api.get('/companies/' + companyId + '/documents-reference')
    .then(r => { const ref = r || {}; configureSleekDate({ date_format: ref.date_format, periods: ref.periods,
      fy_start_day: ref.fy_start_day, fy_start_month: ref.fy_start_month }); }).catch(() => {});

  function toast(m){ note.value = m; setTimeout(() => { if(note.value === m) note.value = ''; }, 4000); }

  // ── Financial years ─────────────────────────────────────────────────────────
  const years = signal([]), yLoading = signal(true), yErr = signal('');
  const armLock = signal(null), armUnlock = signal(null);      // fy_year currently armed
  const closeJournals = signal(false), reason = signal(''), busy = signal(false), adding = signal(false);

  async function loadYears(){
    yLoading.value = true;
    try { const r = await api.get(base + '/years'); years.value = r.years || []; yErr.value = ''; }
    catch(e){ yErr.value = (e.data && e.data.error) || t('gov.err_load', 'Could not load the financial years'); }
    yLoading.value = false;
  }
  loadYears();

  async function addYear(){
    adding.value = true;
    try { const r = await api.post(base + '/years', {}); await loadYears();
          toast(t('gov.toast_added', 'Added FY') + ' ' + (r && r.fy_year)); }
    catch(e){ toast((e.data && e.data.error) || t('gov.err_add', 'Could not add a financial year')); }
    adding.value = false;
  }
  // Refresh the years list without losing the reader's place: the list re-renders when
  // years.value changes, which can clamp .shell-main's scroll — capture it and restore.
  async function reloadYearsKeepingScroll(){
    const y0 = getScroll();
    await loadYears();
    setScroll(y0);                       // the re-render is synchronous on the signal write…
    setTimeout(() => setScroll(y0), 0);  // …and again once layout settles
  }
  async function doLock(fy){
    busy.value = true;
    try {
      // NO reason on lock. close=true posts the balanced year-end closing journals.
      await api.post(base + '/years/' + fy + '/lock', { close: closeJournals.value });
      armLock.value = null; closeJournals.value = false; await reloadYearsKeepingScroll();
      toast('FY ' + fy + t('gov.toast_locked', ' locked — nothing in it can change until unlocked'));
    } catch(e){ toast((e.data && e.data.error) || t('gov.err_lock', 'Could not lock the year')); }
    busy.value = false;
  }
  async function doUnlock(fy){
    const r = reason.value.trim();
    if(!r){ toast(t('gov.err_reason', 'A reason is required to unlock a financial year')); return; }  // mirror the API
    busy.value = true;
    try {
      await api.post(base + '/years/' + fy + '/unlock', { reason: r });
      armUnlock.value = null; reason.value = ''; await reloadYearsKeepingScroll();
      toast('FY ' + fy + t('gov.toast_unlocked', ' unlocked'));
    } catch(e){ toast((e.data && e.data.error) || t('gov.err_unlock', 'Could not unlock the year')); }
    busy.value = false;
  }

  // The per-row action cell — Lock / arm-lock / Unlock / arm-unlock (reason+confirm). It is the
  // ONLY thing that reads armLock / armUnlock / busy, and it is mounted as its own reactive
  // ${() => …} block per row (see yearsView). So clicking Unlock flips just that row's cell inline
  // — the card list is NOT remounted (scroll + sibling rows stay put). It must NOT read
  // reason.value / closeJournals.value: those live only in .value / ?checked / ?disabled bindings,
  // so typing the reason never recreates the input (keeps focus).
  const yearActionCell = (y, gov) => {
    const aL = armLock.value, aU = armUnlock.value, b = busy.value;
    if(!gov){
      return y.locked || y.partially_locked
        ? html`<span class="muted gov-reason">${y.reason ? '“' + y.reason + '”' : t('gov.locked', 'locked')}</span>`
        : '';
    }
    if(y.locked || y.partially_locked){
      return aU === y.fy_year
        ? html`<div class="gov-arm">
            <input class="form-control gov-reason-in" .value=${reason}
              @input=${e => { reason.value = e.target.value; }}
              placeholder=${t('gov.reason_placeholder', 'Reason (required)')}>
            <button class="btn btn-primary btn-sm" ?disabled=${() => busy.value || !reason.value.trim()} @click=${() => doUnlock(y.fy_year)}>${() => busy.value ? t('gov.unlocking', 'Unlocking…') : t('gov.unlock', 'Unlock')}</button>
            <button class="btn btn-outline btn-sm" @click=${() => { armUnlock.value = null; }}>${t('common.cancel', 'Cancel')}</button>
          </div>`
        : html`<div class="gov-lockinfo">${y.reason ? html`<span class="muted gov-reason">“${y.reason}”</span>` : ''}
            <button class="btn btn-outline btn-sm" ?disabled=${b} @click=${() => { armUnlock.value = y.fy_year; reason.value = ''; }}>${t('gov.unlock', 'Unlock')}</button></div>`;
    }
    return aL === y.fy_year
      ? html`<div class="gov-arm">
          <label class="gov-close-opt"><input type="checkbox" ?checked=${closeJournals} @change=${e => { closeJournals.value = e.target.checked; }}> ${t('gov.close_opt', 'Post year-end closing journals')}</label>
          <button class="btn btn-primary btn-sm" ?disabled=${b} @click=${() => doLock(y.fy_year)}>${() => busy.value ? t('gov.locking', 'Locking…') : t('gov.lock_year', 'Lock year')}</button>
          <button class="btn btn-outline btn-sm" @click=${() => { armLock.value = null; closeJournals.value = false; }}>${t('common.cancel', 'Cancel')}</button>
        </div>`
      : html`<button class="btn btn-outline btn-sm" @click=${() => { armLock.value = y.fy_year; closeJournals.value = false; }}>${t('gov.lock_button', '🔒 Lock')}</button>`;
  };

  // yearsView is STRUCTURAL: it reads years / yLoading / yErr / caps only — NOT armLock / armUnlock
  // / busy. The card list is therefore rebuilt only when the data reloads, never when a row is
  // armed. Each row's controls live in an inner ${() => yearActionCell(y, gov)} block so arming
  // touches only that row's action cell in place (inline unlock — keeps scroll + focus).
  const yearsView = () => {
    if(yLoading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(yErr.value) return html`<div class="muted-block">${yErr.value}</div>`;
    const list = years.value, gov = canGovern();
    if(!list.length) return html`<div class="muted-block">${t('gov.empty', 'No financial years yet — they appear with the first transaction, or add one with “+ Add financial year”.')}</div>`;
    return html`<div class="gov-list">${list.map(y => html`
      <div class=${'card gov-card' + (y.locked ? ' gov-locked' : (y.partially_locked ? ' gov-partial' : ''))}><div class="card-body gov-row">
        <div class="gov-year">
          <span class=${'gov-lock ' + (y.locked ? 'gov-lock-red' : (y.partially_locked ? 'gov-lock-partial' : 'gov-lock-open'))}>${y.locked || y.partially_locked ? '🔒' : '🔓'}</span>
          <span class="gov-fy">FY ${y.fy_year}</span>
          ${y.is_current ? html`<span class="gov-badge">${t('common.current', 'current')}</span>` : ''}
          ${y.locked ? html`<span class="gov-badge gov-badge-locked" title=${t('gov.locked_hint', 'This year is locked — nothing dated in it can be added, changed or deleted until it is unlocked.')}>${t('gov.locked', 'locked')}</span>` : ''}
          ${y.partially_locked && !y.locked ? html`<span class="gov-badge gov-badge-warn" title=${t('gov.partly_hint', "Some periods in this year are locked, but the year isn't fully locked — usually a leftover from a financial-year change. Unlock to clear it.")}>${t('gov.partly', 'partly locked')}</span>` : ''}
        </div>
        <div class="gov-range mono">${y.start} → ${y.end}</div>
        <div class="gov-counts muted">${y.journals} journal${y.journals === 1 ? '' : 's'} · ${y.posted} posted</div>
        <div class="gov-act">${() => yearActionCell(y, gov)}</div>
      </div></div>`)}
    </div>`;
  };

  // ── VAT returns (VAT201 model) ────────────────────────────────────────────────
  const vFrom = signal(isoMonthStart()), vTo = signal(isoToday()), vRef = signal('');
  const preview = signal(null), pErr = signal(''), pLoading = signal(false), filing = signal(false);
  const subs = signal([]), sLoading = signal(true), sErr = signal('');
  const armRev = signal(null), revReason = signal(''), reversing = signal(false);

  async function loadSubs(){
    sLoading.value = true;
    try { const r = await api.get(base + '/vat/submissions'); subs.value = r.data || []; sErr.value = ''; }
    catch(e){ sErr.value = (e.data && e.data.error) || t('gov.vat_err_list', 'Could not load VAT returns'); }
    sLoading.value = false;
  }
  loadSubs();

  async function doPreview(){
    if(!vFrom.value || !vTo.value){ toast(t('gov.vat_dates', 'Pick a from and to date for the return')); return; }
    pLoading.value = true; pErr.value = ''; preview.value = null;
    try { preview.value = await api.get(base + '/vat/next?from=' + vFrom.value + '&to=' + vTo.value); }
    catch(e){ pErr.value = (e.data && e.data.error) || t('gov.vat_err_preview', 'Could not build the return'); }
    pLoading.value = false;
  }
  async function doFile(){
    if(!vFrom.value || !vTo.value){ toast(t('gov.vat_dates', 'Pick a from and to date for the return')); return; }
    filing.value = true;
    try {
      const r = await api.post(base + '/vat/submissions', { from: vFrom.value, to: vTo.value, reference: vRef.value.trim() || undefined });
      vRef.value = ''; preview.value = null;
      await loadSubs();
      toast(t('gov.vat_filed', 'VAT return filed — it is now locked') + ' (' + (r.claimed_lines || 0) + ' ' + t('gov.vat_lines', 'lines') + ')');
    } catch(e){ toast((e.data && e.data.error) || t('gov.vat_err_file', 'Could not file the return')); }
    filing.value = false;
  }
  async function doReverse(id){
    const r = revReason.value.trim();
    if(!r){ toast(t('gov.vat_reason', 'A reason is required to reverse a VAT return')); return; }  // mirror the API
    reversing.value = true;
    try { await api.post(base + '/vat/submissions/' + id + '/reverse', { reason: r });
          armRev.value = null; revReason.value = ''; await loadSubs();
          toast(t('gov.vat_reversed', 'VAT return reversed — its lines roll into the next return')); }
    catch(e){ toast((e.data && e.data.error) || t('gov.vat_err_reverse', 'Could not reverse the return')); }
    reversing.value = false;
  }

  // A return's net says which way the money goes, as the VAT Report does: fmtMoney shows the amount without
  // its sign, so the label carries it — a negative net is a refund (owner 2026-10-08; it read "Net payable").
  const vatNetLabel = net => Number(net) >= 0
    ? t('report.vat.amount_owing', 'Total amount owing') : t('report.vat.refund_due', 'Total refund due');

  // preview result block. Reads preview / pLoading / pErr / caps. Does NOT read vRef, so typing the
  // reference never recreates this block (keeps focus).
  const previewView = () => {
    if(pLoading.value) return html`<div class="muted-block">${t('gov.vat_building', 'Building the return…')}</div>`;
    if(pErr.value) return html`<div class="muted-block">${pErr.value}</div>`;
    const p = preview.value;
    if(!p) return html`<div class="muted gov-vat-hint">${t('gov.vat_prompt', 'Pick a period and Preview to see the next open return.')}</div>`;
    const adj = p.prior_period_adjustments || [];
    return html`
      <div class="gov-vat-totals">
        <div class="gov-vat-fig"><span class="muted">${t('gov.vat_output', 'Output VAT')}</span><span class="mono">${fmtMoney(p.output_tax, undefined, true)}</span></div>
        <div class="gov-vat-fig"><span class="muted">${t('gov.vat_input', 'Input VAT')}</span><span class="mono">${fmtMoney(p.input_tax)}</span></div>
        <div class="gov-vat-fig gov-vat-net"><span class="muted">${vatNetLabel(p.net_tax)}</span><span class="mono">${fmtMoney(p.net_tax)}</span></div>
        <div class="gov-vat-fig"><span class="muted">${t('gov.vat_linecount', 'Lines')}</span><span class="mono">${(p.lines || []).length}</span></div>
      </div>
      ${adj.length ? html`<div class="gov-note gov-vat-adj" data-vat-adjustments>
        <strong>${t('gov.vat_adj_title', 'Prior-period adjustments swept forward')}</strong>
        <p class="muted">${t('gov.vat_adj_msg', 'These entries were dated in an already-submitted period. A filed return is never reopened — the adjustment flows into THIS (the next) return instead (SARS VAT201 model).')}</p>
        <ul class="gov-vat-adjlist">${adj.map(a => html`<li><span class="mono">${a.journal_date}</span> · ${a.tax_code || ''} · <span class="mono">${fmtMoney(a.tax_amount)}</span></li>`)}</ul>
      </div>` : ''}
      ${() => canGovern() ? html`<div class="gov-vat-file">
        <input class="form-control gov-vat-ref" .value=${vRef} @input=${e => { vRef.value = e.target.value; }} placeholder=${t('gov.vat_ref_ph', 'Reference (optional)')}>
        <button class="btn btn-primary btn-sm" ?disabled=${() => filing.value} @click=${doFile}>${() => filing.value ? t('gov.vat_filing', 'Filing…') : t('gov.vat_file', 'File this return')}</button>
      </div>` : ''}`;
  };

  // A return is locked once submitted, and stays locked once marked filed with the tax authority (status
  // 'filed', API migration 170): the API freezes its journals and period for both statuses, in every country.
  // Whether it can still be reversed depends on the company (a South African one can; one with voucher
  // integrity switched on, as in Finland, cannot), so the API says so per return: `reversible`. An older API
  // sends no such field: then only a submitted return offers Reverse, as before.
  const vatLocked = s => s.status === 'submitted' || s.status === 'filed';
  const vatReversible = s => typeof s.reversible === 'boolean' ? s.reversible : s.status === 'submitted';

  // submitted-history block. Reads subs / sLoading / sErr / armRev / reversing / caps. Does NOT read
  // revReason, so typing the reverse reason never recreates the input.
  const subsView = () => {
    if(sLoading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(sErr.value) return html`<div class="muted-block">${sErr.value}</div>`;
    const list = subs.value, ar = armRev.value, rv = reversing.value, gov = canGovern();
    if(!list.length) return html`<div class="muted-block">${t('gov.vat_none', 'No VAT returns filed yet.')}</div>`;
    return html`<div class="gov-list">${list.map(s => html`
      <div class=${'card gov-card' + (vatLocked(s) ? ' gov-locked' : '')}><div class="card-body gov-row">
        <div class="gov-year">
          <span class=${'gov-lock' + (vatLocked(s) ? ' gov-lock-red' : '')}>${vatLocked(s) ? '🔒' : '🔓'}</span>
          <span class="gov-fy">${s.period_from} → ${s.period_to}</span>
          ${s.status === 'filed'
            ? html`<span class="gov-badge gov-badge-locked" title=${t('gov.vat_filed_hint', 'This return is marked filed with the tax authority. It stays locked — its journals and period are frozen. Any later adjustment flows into the next return.')}>${t('gov.vat_filed_badge', 'filed')}</span>`
            : s.status === 'submitted'
            ? html`<span class="gov-badge gov-badge-locked" title=${t('gov.vat_locked_hint', 'This return is filed and locked — its journals and period are frozen. Any later adjustment flows into the next return.')}>${t('gov.vat_locked', 'locked')}</span>`
            : html`<span class="gov-badge gov-badge-warn">${s.status}</span>`}
          ${s.reference ? html`<span class="muted gov-reason">${s.reference}</span>` : ''}
        </div>
        <div class="gov-range mono">${vatNetLabel(s.net_tax)} ${fmtMoney(s.net_tax)}</div>
        <div class="gov-counts muted">${s.submitted_at ? (t('gov.vat_filed_on', 'filed') + ' ' + String(s.submitted_at).slice(0, 10)) : ''}${s.reversal_reason ? ' · “' + s.reversal_reason + '”' : ''}</div>
        <div class="gov-act">
          ${gov && vatReversible(s)
            ? (ar === s.id
                ? html`<div class="gov-arm">
                    <input class="form-control gov-reason-in" .value=${revReason} @input=${e => { revReason.value = e.target.value; }} placeholder=${t('gov.reason_placeholder', 'Reason (required)')}>
                    <button class="btn btn-primary btn-sm" ?disabled=${() => rv || !revReason.value.trim()} @click=${() => doReverse(s.id)}>${() => rv ? t('gov.vat_reversing', 'Reversing…') : t('gov.vat_reverse', 'Reverse')}</button>
                    <button class="btn btn-outline btn-sm" @click=${() => { armRev.value = null; }}>${t('common.cancel', 'Cancel')}</button>
                  </div>`
                : html`<button class="btn btn-outline btn-sm" @click=${() => { armRev.value = s.id; revReason.value = ''; }}>${t('gov.vat_reverse', 'Reverse')}</button>`)
            : ''}
        </div>
      </div></div>`)}
    </div>`;
  };

  const vatView = () => html`<div class="gov-vat">
    <div class="card"><div class="card-body">
      <h3 class="gov-vat-h">${t('gov.vat_next_title', 'Next VAT return')}</h3>
      <p class="page-sub">${t('gov.vat_next_sub', 'A filed return is immutable — its journals and period freeze. Anything you change afterwards is never applied to a filed return; it is swept into the next open return as an adjustment (VAT201 model).')}</p>
      <div class="gov-vat-period">
        <label class="gov-vat-lbl">${t('gov.vat_from', 'From')} <sleek-date data-free="true" class="form-control" .value=${vFrom} @change=${e => { vFrom.value = e.target.value; }}></sleek-date></label>
        <label class="gov-vat-lbl">${t('gov.vat_to', 'To')} <sleek-date data-free="true" class="form-control" .value=${vTo} @change=${e => { vTo.value = e.target.value; }}></sleek-date></label>
        <button class="btn btn-outline btn-sm" ?disabled=${() => pLoading.value} @click=${doPreview}>${() => pLoading.value ? t('gov.vat_building', 'Building…') : t('gov.vat_preview', 'Preview')}</button>
      </div>
      ${previewView}
    </div></div>
    <div class="card"><div class="card-body">
      <h3 class="gov-vat-h">${t('gov.vat_history_title', 'Submitted returns')}</h3>
      ${subsView}
    </div></div>
  </div>`;

  const bodyView = () => tab.value === 'vat' ? vatView() : yearsView();

  return tenantShell(companyId, 'Governance', html`<div class="page">
    <div class="page-back"><button type="button" class="btn btn-link back-link" @click=${() => leaveGovernance(companyId)}>${icon('chevron-left', {size: 14})} ${() => t('common.back', 'Back')}</button></div>
    <div class="page-head with-action"><div><h1 class="page-title">${() => t('gov.title', 'Financial governance')}</h1>
      <div class="page-sub">${() => t('gov.subtitle', 'Lock a financial year to mark it submitted to the receiver — nothing in it can change until it is unlocked. File a VAT return to freeze its period. Every lock, unlock and reversal is recorded.')}</div></div>
      ${() => tab.value === 'years' && canGovern()
        ? html`<button class="btn btn-primary" ?disabled=${() => adding.value} @click=${addYear}>${() => adding.value ? t('common.adding', 'Adding…') : t('gov.add_year', '+ Add financial year')}</button>`
        : ''}</div>
    <div class="segs subtabs gov-tabs">
      <button class=${() => 'seg' + (tab.value === 'years' ? ' on' : '')} @click=${() => { tab.value = 'years'; }}>${() => t('gov.tab_years', 'Financial years')}</button>
      <button class=${() => 'seg' + (tab.value === 'vat' ? ' on' : '')} @click=${() => { tab.value = 'vat'; }}>${() => t('gov.tab_vat', 'VAT returns')}</button>
    </div>
    ${() => note.value ? html`<div class="gov-note">${note.value}</div>` : ''}
    ${bodyView}
  </div>`);
}
